import React, { useEffect, useRef, useState } from "react";
import { connect, useSelector } from "react-redux";
import styled, { css } from "styled-components";

import { Spin, Tooltip, Typography, Space, Button } from "antd";
import { useTranslation } from "react-i18next";
import { createStructuredSelector } from "reselect";
import { fontWeights, sizes } from "../../constant/styles";
import { WHITE, BLACK, GRAY_900, GRAY_500, GRAY_700, GRAY_400 } from "../../constant/colors";
import { ReactComponent as AlertTrimmed } from "../../assets/svg/alert-circle-trimmed.svg";
import ACTIONS from "../../store/action";
import { ReactComponent as UserIcon } from "../../assets/svg/user-icon.svg";
import {
  selectUser,
  selectedFilterListType,
  selectSorting,
  selectAllUsersAndUserGroups,
  selectUserGroupsWithUsers
} from "../../store/selector";
import ErrorMessage from "./customMessages/ErrorMessage";
import { compose } from "redux";
import ActiveStarIcon from "../../assets/svg/ActiveStarIcon";
import InActiveStarIcon from "../../assets/svg/InActiveStarIcon";
import { starFilterList } from "../../api/api";
import { LoadingOutlined } from "@ant-design/icons";
import { filtersTypes } from "../../constant/savedList";
import { useGetSetting } from "../../hooks/getSetting";
import { displayedMRN } from "@tailormed/common-client/util/mrn";
import { formatPhoneNumber } from "../../utils/formaters";
import { customerTypes } from "../../constant/customerType";
import { ASCEND, DECEND, SORT_DIRECTIONS } from "../../constant/sort";
import "./CommonColumns.css";
import { ViewersBadge, TypeIconBadge } from "../../utils/savedList";

export const maxNumberOfRecords = 10000;
const { Text } = Typography;

const StyledButton = styled(Button)`
  ${({ isFilterListView, starred }) =>
    isFilterListView &&
    css`
      :hover {
        background-color: transparent;
      }
      svg {
        path {
          stroke: ${starred ? "none" : GRAY_400};
        }
      }
    `}
`;

export const StarFilterListColumnComponent = ({
  setSavedListsTableRecordData,
  selectedListType,
  record,
  page,
  searchSavedListAct,
  onStarChange,
  isFilterListView
}) => {
  const [isFetching, setIsFetching] = useState(false);
  const { t } = useTranslation();
  const title = t(record.starred ? "savedList.favorites" : "savedList.mark_as_favorite");

  const handleStarFilterList = async (filterListId, newValue) => {
    try {
      setIsFetching(true);
      // Call optional callback for updating filterList in other contexts
      if (onStarChange) {
        await onStarChange(newValue);
        return;
      }
      await starFilterList(filterListId, newValue);
      if (selectedListType === filtersTypes.starred) {
        searchSavedListAct(page);
      } else {
        setSavedListsTableRecordData({ ...record, starred: newValue });
      }
    } catch (error) {
      ErrorMessage(`${t("errorMessages.filterlist_starred")}`);
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <div onClick={(event) => event.stopPropagation()}>
      {isFetching && <Spin indicator={<LoadingOutlined style={{ fontSize: 18 }} spin />} />}
      {!isFetching && (
        <StyledButton
          isFilterListView={isFilterListView}
          starred={record.starred}
          type="text"
          id={record.starred ? "remove_from_favorite" : "mark_list_as_favorite"}
          onClick={() => handleStarFilterList(record.id, !record.starred)}
          style={{ padding: 0 }}
        >
          <Tooltip
            title={
              <Text style={{ color: BLACK, fontSize: sizes.small, fontWeight: fontWeights.semibold }}>{t(title)}</Text>
            }
            color={WHITE}
            placement="top"
            arrow={{
              pointAtCenter: true
            }}
          >
            <div style={{ width: 18, height: 18, display: isFilterListView ? "flex" : "block" }}>
              {record.starred ? <ActiveStarIcon /> : <InActiveStarIcon />}
            </div>
          </Tooltip>
        </StyledButton>
      )}
    </div>
  );
};

export const ListNameColumn = ({ record }) => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const assigneesAndUserGroups = useSelector(selectAllUsersAndUserGroups);
  const userGroupsWithUsers = useSelector(selectUserGroupsWithUsers);

  const getInvalidMessage = () => {
    const isOwner = user?.id === record?.user;
    if (isOwner) {
      return t("savedList.owner_in_valid_list");
    } else {
      return t("savedList.non_owner_in_valid_list");
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "row", gap: "12px", alignItems: "center" }}>
      <TypeIconBadge record={record} />

      <div style={{ display: "flex", flexDirection: "column", gap: "0px" }}>
        <Space>
          <Text
            style={{
              fontWeight: fontWeights.semibold,
              fontSize: sizes.medium,
              color: GRAY_900
            }}
          >
            {record.name ? record.name : t("savedList.no_list_name")}
          </Text>
          {!record.isValid && (
            <Tooltip
              overlayInnerStyle={{
                backgroundColor: BLACK,
                borderRadius: 5,
                padding: "15px"
              }}
              overlayStyle={{
                maxWidth: "350px"
              }}
              autoAdjustOverflow={false}
              title={<Text style={{ color: WHITE, fontWeight: fontWeights.medium }}>{getInvalidMessage()}</Text>}
            >
              <div style={{ paddingTop: "1.5px", position: "relative", display: "flex", alignItems: "center" }}>
                <AlertTrimmed />
              </div>
            </Tooltip>
          )}
        </Space>

        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "25px" }}>
          <Text style={{ fontSize: sizes.medium, color: GRAY_500, fontWeight: fontWeights.regular }}>
            {t("savedList.description", {
              view: "Orders"
            })}
          </Text>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <UserIcon />
            <Text style={{ fontSize: sizes.medium, color: GRAY_500, fontWeight: fontWeights.regular }}>
              {t("savedList.created_by")} {record.username}
            </Text>
          </div>

          <ViewersBadge
            record={record}
            assigneesAndUserGroups={assigneesAndUserGroups}
            userGroupsWithUsers={userGroupsWithUsers}
            t={t}
          />
        </div>
      </div>
    </div>
  );
};

export const NumberOfRecordsColumn = ({ record, savedListsRecordCounts }) => {
  const { t } = useTranslation();
  const currentRecord = savedListsRecordCounts[record?.id];

  if (!currentRecord) return <Spin />;

  if (currentRecord.success === false) {
    return <Text> {t("savedList.no_records")} </Text>;
  } else if (currentRecord.success) {
    return (
      <Text>{currentRecord.total > maxNumberOfRecords ? t("savedList.more_than_10000") : currentRecord.total}</Text>
    );
  }

  return null;
};

const starFilterMapStateToProps = createStructuredSelector({
  selectedListType: selectedFilterListType
});

const starFilterMapDispatchToProps = (dispatch) => ({
  searchSavedListAct: (page) => dispatch(ACTIONS.searchSavedListAction(page)),
  setSavedListsTableRecordData: (record) => dispatch(ACTIONS.setSavedListsTableRecordData(record))
});
export const StarFilterListColumn = compose(connect(starFilterMapStateToProps, starFilterMapDispatchToProps))(
  StarFilterListColumnComponent
);

export const PatientNameColumn = ({ record }) => {
  const { t } = useTranslation();
  const [prefix, customerType] = useGetSetting(["id_prefix", "fe-customer-type"]);
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <Text
        style={{
          fontWeight: fontWeights.semibold,
          fontSize: sizes.medium,
          color: GRAY_900
        }}
        id="patient_name"
      >
        {record.name ? record.name : "Patient"}
      </Text>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "5px",
          alignItems: "center",
          fontSize: sizes.small,
          fontWeight: fontWeights.regular
        }}
      >
        {displayedMRN(record.mrn) && (
          <Text style={{ color: GRAY_500 }} id="patient_mrn">
            {customerType.toLowerCase() === customerTypes.RETAIL
              ? t("columns.record_phone", { mrnRecord: formatPhoneNumber(record.mrn) })
              : t("columns.record_mrn", { mrnRecord: displayedMRN(record.mrn) })}
          </Text>
        )}
        <Text style={{ color: GRAY_500 }} id="patient_id">{`(${prefix ? prefix.toUpperCase() : "TM"}-${
          record.id
        })`}</Text>
      </div>
    </div>
  );
};

export const withSorting = (tableColumn, { sorterTooltipText, ...rest }, sortDirection) => {
  const { t } = useTranslation();
  const sorter = useSelector(selectSorting);

  const sortingActionTooltip =
    sorterTooltipText ||
    (() => {
      if (sorter?.columnKey === tableColumn.key) {
        if (sorter?.order === DECEND) {
          return t("columns.click_to_cancel_sorting");
        }
        if (sorter?.order === ASCEND) {
          return t("columns.click_to_sort_descending");
        }
      }
      return t("columns.click_to_sort_ascending");
    })();

  return {
    sorter: true,
    sortDirections: sortDirection ? sortDirection : SORT_DIRECTIONS,
    ...tableColumn,
    title: () => (
      <div style={{ display: "flex", gap: "5px" }} id={`columnـ${tableColumn.id}`}>
        <Tooltip title={sortingActionTooltip}>
          <Text style={{ color: GRAY_700 }}>{tableColumn.title}</Text>
        </Tooltip>
      </div>
    ),
    showSorterTooltip: false,
    ...rest,
    defaultSortOrder: sorter?.column && sorter?.column?.key === tableColumn.key && sorter?.order
  };
};

export const AutoEllipsisTooltipColumnValue = ({ children }) => {
  const textRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        const isOverflowing = textRef.current.scrollWidth > textRef.current.clientWidth;
        setShowTooltip(isOverflowing);
      }
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [children]);

  return (
    <Tooltip
      title={children}
      placement="topLeft"
      open={showTooltip ? undefined : false}
      color={WHITE}
      overlayInnerStyle={{ color: BLACK }}
    >
      <div
        ref={textRef}
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: "100%"
        }}
      >
        {children}
      </div>
    </Tooltip>
  );
};
